import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

const testimonials = [
  {
    name: '漫画爱好者小陈',
    role: '资深漫画读者',
    content: '无翼鸟漫画是我发现优质漫画内容的首选平台。分类清晰，推荐精准，每次都能找到让我惊喜的新作品。强烈推荐给所有漫画爱好者！',
    rating: 5,
  },
  {
    name: '独立漫画家阿明',
    role: '原创漫画作者',
    content: '作为一名独立漫画创作者，我在无翼鸟漫画上学到了很多创作技巧。创作教程栏目非常实用，分镜和角色设计的内容尤其有帮助。',
    rating: 5,
  },
  {
    name: '学生小美',
    role: '漫画入门学习者',
    content: '从零开始学漫画的时候找到了这个网站，教程内容通俗易懂，让我快速掌握了基础绘画技巧。现在已经能独立完成短篇漫画了！',
    rating: 5,
  },
  {
    name: '漫画社团社长',
    role: '大学漫画社负责人',
    content: '我们社团经常用无翼鸟漫画上的推荐文章作为社团活动的讨论素材。资源导航功能特别好用，帮我们节省了大量找漫画的时间。',
    rating: 5,
  },
  {
    name: '插画师小林',
    role: '职业插画师',
    content: '无翼鸟漫画的内容质量非常高，无论是风格解析还是创作教程都非常专业。在这里找到了很多创作灵感，受益良多。',
    rating: 5,
  },
  {
    name: '家长张女士',
    role: '漫画爱好者家长',
    content: '孩子喜欢看漫画，我通过这个网站帮他筛选适合年龄的优质漫画。分类详细，内容安全，让我很放心。推荐给有同样需求的家长。',
    rating: 4,
  },
];

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '无翼鸟漫画',
  url: 'https://wingless-bird-manga.readdy.ai',
  description: '致力于为漫画爱好者提供优质的漫画资源导航、阅读推荐与创作指南。发现好漫画，从这里开始。',
  foundingDate: '2023',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9tf4ml3pcjqs2fcsct0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="无翼鸟漫画致力于为漫画爱好者提供优质的漫画资源导航、阅读推荐与创作指南。了解我们的品牌故事、用户口碑及联系方式。"
        keywords="无翼鸟漫画,关于我们,漫画导航,漫画推荐,漫画资源,联系我们"
        canonicalUrl="/about"
        structuredData={orgSchema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Page Header */}
        <section className="pt-24 pb-8 md:pt-28 md:pb-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mt-4">关于我们</h1>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="w-16 h-16 flex items-center justify-center rounded-xl bg-primary-500 text-background-50 font-bold text-2xl mx-auto mb-5">翼</span>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">无翼鸟漫画 - 品牌介绍</h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
                无翼鸟漫画成立于2023年，是一个专注于漫画资源导航与阅读推荐的综合性平台。我们致力于为漫画爱好者提供高质量的漫画内容发现和阅读体验。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              <div className="p-6 bg-background-50 border border-background-200/70 rounded-lg text-center">
                <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mx-auto mb-4">
                  <i className="ri-compass-3-line text-xl"></i>
                </span>
                <h3 className="font-semibold text-foreground-950 mb-2">精准导航</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  提供清晰的漫画分类导航，帮助用户快速找到感兴趣的漫画类型和作品。
                </p>
              </div>
              <div className="p-6 bg-background-50 border border-background-200/70 rounded-lg text-center">
                <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mx-auto mb-4">
                  <i className="ri-star-line text-xl"></i>
                </span>
                <h3 className="font-semibold text-foreground-950 mb-2">优质推荐</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  精心筛选优质漫画内容，从海量作品中为你推荐最值得阅读的漫画。
                </p>
              </div>
              <div className="p-6 bg-background-50 border border-background-200/70 rounded-lg text-center">
                <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mx-auto mb-4">
                  <i className="ri-book-open-line text-xl"></i>
                </span>
                <h3 className="font-semibold text-foreground-950 mb-2">创作指南</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  提供专业的漫画创作教程，从零开始教你画出属于自己的漫画作品。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Mission */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">我们的使命</h2>
            <div className="space-y-4">
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                在信息爆炸的时代，漫画爱好者面临着内容选择困难的问题。每天都有大量的新作品涌现，但真正优质的内容往往被淹没在海量信息中。无翼鸟漫画的使命，就是帮助漫画爱好者从信息的海洋中精准找到那些真正值得阅读的优质内容。
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                我们相信，好的漫画不仅是一种娱乐，更是一种艺术表达和情感共鸣。无翼鸟漫画致力于搭建一座桥梁，连接优秀的漫画创作者与热爱漫画的读者。通过专业的分类体系、客观的内容评测和贴心的创作者指导，我们希望能让更多人发现漫画的魅力。
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                未来，我们将持续优化平台功能，扩展内容覆盖面，努力成为中文漫画爱好者最信赖的资源导航与阅读指南平台。
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t, index) => (
                <div key={index} className="p-5 md:p-6 bg-background-50 border border-background-200/70 rounded-lg">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 font-medium text-sm">
                      {t.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2 text-center">联系我们</h2>
            <p className="text-foreground-600 text-sm text-center mb-8">有任何问题或建议？请通过以下表单与我们联系</p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-950 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 border border-background-200/70 rounded-lg text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-950 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 border border-background-200/70 rounded-lg text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-950 mb-1.5">主题</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 border border-background-200/70 rounded-lg text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-950 mb-1.5">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 border border-background-200/70 rounded-lg text-sm text-foreground-950 bg-background-50 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 transition-colors resize-none"
                  placeholder="请输入您的留言（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
              </div>

              {/* Honeypot */}
              <div className="hp-field" aria-hidden="true">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-input"
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 text-center">
                  感谢您的留言！我们会尽快回复您。
                </div>
              )}

              {formStatus === 'error' && formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 text-center">
                  {formError}
                </div>
              )}
            </form>

            {/* Contact Info */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-950">contact@winglessmanga.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-wechat-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">微信公众号</p>
                  <p className="text-sm font-medium text-foreground-950">无翼鸟漫画</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-weibo-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">微博</p>
                  <p className="text-sm font-medium text-foreground-950">@无翼鸟漫画</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}